/**
 * Link classification and utility functions
 * Determines if links are internal/external and how they should behave
 */
export interface LinkClassification {
    isInternal: boolean
    isHelpCenter: boolean
    isExternal: boolean
    shouldOpenInNewTab: boolean
    shouldShowNewWindowIcon: boolean
    url: string
    normalizedUrl: string
}

/**
 * Classifies a URL and determines appropriate link behavior
 */
export const classifyLink = (url: string, currentDomain?: string): LinkClassification => {
    if (!url) {
        return {
            isInternal: false,
            isHelpCenter: false,
            isExternal: true,
            shouldOpenInNewTab: false,
            shouldShowNewWindowIcon: false,
            url: '',
            normalizedUrl: ''
        }
    }

    const normalizedUrl = url.toLowerCase().trim()

    // Get current domain if not provided
    const domain = currentDomain || (typeof window !== "undefined" ? window.location.hostname : "")

    // Check if URL is relative or absolute
    const isRelativeUrl = normalizedUrl.startsWith("/") && !normalizedUrl.startsWith("//")
    const isAbsoluteUrl = normalizedUrl.startsWith("http://") || normalizedUrl.startsWith("https://") || normalizedUrl.startsWith("//")
    const isHashLink = normalizedUrl.startsWith("#")
    const isMailto = normalizedUrl.startsWith("mailto:")
    const isTel = normalizedUrl.startsWith("tel:")

    // Special protocols are external but don't open in new tab
    if (isHashLink || isMailto || isTel) {
        return {
            isInternal: false,
            isHelpCenter: false,
            isExternal: true,
            shouldOpenInNewTab: false,
            shouldShowNewWindowIcon: false,
            url,
            normalizedUrl
        }
    }

    // Relative URLs are internal by definition
    if (isRelativeUrl) {
        const isHelpCenter = normalizedUrl.includes('/help/')
        return {
            isInternal: true,
            isHelpCenter,
            isExternal: false,
            shouldOpenInNewTab: false,
            shouldShowNewWindowIcon: false,
            url,
            normalizedUrl
        }
    }

    // For absolute URLs, check domain
    if (isAbsoluteUrl) {
        try {
            const urlObj = new URL(url.startsWith('//') ? `https:${url}` : url)
            const linkDomain = urlObj.hostname.toLowerCase()

            // Check if it's the same domain or subdomain
            const isSameDomain = linkDomain === domain ||
                linkDomain.endsWith(`.${domain}`) ||
                domain.endsWith(`.${linkDomain}`)

            // Check for help center paths
            const isHelpCenter = urlObj.pathname.toLowerCase().includes('/help/') || urlObj.pathname.toLowerCase().includes('/localhost/')

            if (isHelpCenter) {
                // Help center links are internal (same tab, no icon)
                return {
                    isInternal: true,
                    isHelpCenter: true,
                    isExternal: false,
                    shouldOpenInNewTab: false,
                    shouldShowNewWindowIcon: false,
                    url,
                    normalizedUrl
                }
            } else {
                // All other links are external (new tab, with icon)
                return {
                    isInternal: false,
                    isHelpCenter: false,
                    isExternal: true,
                    shouldOpenInNewTab: true,
                    shouldShowNewWindowIcon: true,
                    url,
                    normalizedUrl
                }
            }
        } catch (error) {
            // Invalid URL - treat as external
            return {
                isInternal: false,
                isHelpCenter: false,
                isExternal: true,
                shouldOpenInNewTab: true,
                shouldShowNewWindowIcon: true,
                url,
                normalizedUrl
            }
        }
    }

    // Default case - treat as external
    return {
        isInternal: false,
        isHelpCenter: false,
        isExternal: true,
        shouldOpenInNewTab: true,
        shouldShowNewWindowIcon: true,
        url,
        normalizedUrl
    }
}
